/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1700179200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Fri Nov 17 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "jira",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Jira",
          "depth": 0,
          "id": "connect-to-jira"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing issues and ideas",
          "depth": 1,
          "id": "syncing-issues-and-ideas"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      ul: "ul",
      li: "li",
      code: "code",
      img: "img",
      h3: "h3",
      h4: "h4"
    }, _provideComponents(), props.components), {PylonLink, NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API documentation"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Issues"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to Jira issues or Product Discovery ideas"
            }), _jsx(_components.td, {
              children: "Upsert, Update, Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developer.atlassian.com/cloud/jira/platform/rest/v2/api-group-issues/#api-group-issues",
                children: "Issues"
              })
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-jira",
        children: "Connect to Jira"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Jira"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to Jira by entering your ", _jsx(_components.strong, {
          children: "Jira Domain"
        }), ", ", _jsx(_components.strong, {
          children: "Username"
        }), ", and ", _jsx(_components.strong, {
          children: "API Token"
        }), " from your Jira account:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["the ", _jsx(_components.strong, {
            children: "Jira Domain"
          }), " is the text before ", _jsx(_components.code, {
            children: ".atlassian.net"
          }), " in the URL,"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Username"
          }), " is the email you use to login to Jira,"]
        }), "\n", _jsxs(_components.li, {
          children: ["and if you don't already have an ", _jsx(_components.strong, {
            children: "API Token"
          }), ", you can create one in your Jira account by navigating to ", _jsx(_components.strong, {
            children: "Account Settings"
          }), " > ", _jsx(_components.strong, {
            children: "Security"
          }), " > ", _jsx(_components.strong, {
            children: "Create and manage API tokens"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-jira-setup.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Jira destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Jira destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-issues-and-ideas",
        children: "Syncing issues and ideas"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch supports syncing to Jira ", _jsx(_components.a, {
          href: "https://support.atlassian.com/jira-software-cloud/docs/what-is-an-issue/",
          children: "issues"
        }), " and ", _jsx(_components.a, {
          href: "https://support.atlassian.com/jira-product-discovery/docs/what-is-an-idea/",
          children: "Product Discovery ideas"
        }), ". If you want to sync to Product Discovery ideas, select a ", _jsx(_components.a, {
          href: "https://www.atlassian.com/software/jira/product-discovery",
          children: _jsx(_components.strong, {
            children: "Product Discovery Project"
          })
        }), " in your sync configuration and use ", _jsx(_components.a, {
          href: "#supported-syncing",
          children: "Insert"
        }), " mode."]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["Upsert and Update mode are only supported for Jira issues. They require you to create a ", _jsx(_components.strong, {
          children: "custom\nfield"
        }), " in Jira to use for record matching in Hightouch. This field needs to be set to the ", _jsx(_components.a, {
          href: "https://support.atlassian.com/jira-cloud-administration/docs/custom-fields-types-in-company-managed-projects/#Advanced-custom-field-types",
          children: _jsx(_components.strong, {
            children: "Text Field (read only)"
          })
        }), " type."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-jira-customfield-mapping.png",
          alt: ""
        })
      }), "\n", _jsxs(_components.p, {
        children: ["To create a ", _jsx(_components.strong, {
          children: "custom field"
        }), " in Jira:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Navigate to ", _jsx(_components.strong, {
              children: "Settings"
            }), " > ", _jsx(_components.strong, {
              children: "Issues"
            }), " > ", _jsx(_components.strong, {
              children: "Custom Fields"
            }), ".\n", _jsx(_components.img, {
              src: "destinations/destination-jira-create-customfield-1.png",
              alt: ""
            })]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Click ", _jsx(_components.strong, {
              children: "Create custom field"
            }), " and select the ", _jsx(_components.a, {
              href: "https://support.atlassian.com/jira-cloud-administration/docs/custom-fields-types-in-company-managed-projects/#Advanced-custom-field-types",
              children: _jsx(_components.strong, {
                children: "Text Field (read only)"
              })
            }), " field type.\n", _jsx(_components.img, {
              src: "destinations/destination-jira-create-customfield-2.png",
              alt: ""
            })]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["Ensure all issue fields and custom fields that you want to sync to are\nincluded on your project's screen. To add custom fields to the\nscreen, navigate to the custom field, click the three-dot menu and then ", _jsx(_components.strong, {
            children: "Associate to Screens"
          }), "."]
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-jira-add-screen.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["You can map model columns to all Jira issue fields that are available to the ", _jsx(_components.strong, {
          children: "Project"
        }), " and ", _jsx(_components.strong, {
          children: "Issue Type"
        }), " you selected in your sync configuration. To sync successfully, ensure the ", _jsx(_components.strong, {
          children: "Description"
        }), " field is in ", _jsx(_components.a, {
          href: "https://developer.atlassian.com/cloud/jira/platform/apis/document/structure/",
          children: "Atlassian Document Format"
        }), " and that these other fields are of these types:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "parent"
          }), " > ", _jsx(_components.code, {
            children: "id"
          })]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "priority"
          }), " > ", _jsx(_components.code, {
            children: "name"
          }), " (for example, Highest, High, Medium, Low, or Lowest)"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "creator"
          }), " > ", _jsx(_components.code, {
            children: "accountId"
          })]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "assignee"
          }), " > ", _jsx(_components.code, {
            children: "accountId"
          })]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "reporter"
          }), " > ", _jsx(_components.code, {
            children: "accountId"
          })]
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Hightouch doesn't currently support syncing to ", _jsx(_components.a, {
            href: "https://confluence.atlassian.com/adminjiraserver/defining-status-field-values-938847108.html",
            children: "Status fields"
          }), ", so this needs to be done manually in the Jira dashboard. Please ", _jsx(PylonLink, {
            pylonText: "Hello, I would like to submit a Feature Request for the Jira destination: syncing to the status field.",
            linkText: "reach out to us"
          }), " ", " if you're interested in this feature."]
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-jira-fieldmappings.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
